/**
 * IdleMode overlay (Plan 01-13 shell + Plan 01-14 tile fill-in
 *                   + Plan 02-17 chore-engagement upgrade).
 *
 * Always-mounted overlay covering the dashboard when `isIdle === true`.
 * DASH-01 locks both modes simultaneously mounted so the clock + timer
 * state survives every transition.  DASH-03 — any pointerdown wakes.
 *
 * Plan 14 fills the 7 placeholder slots Plan 13 declared with the real
 * tile components.  DuskTheme is a headless effect-host rendered outside
 * the grid that toggles `html.theme-dusk` based on /api/sun/today + admin
 * override (DASH-05).
 *
 * Plan 02-17 mounts the ApprovalSlaStrip. The strip is driven by
 * useTabletSlaStrip — when count=0 the strip renders null.
 *
 * #160 (Stell_Kjokken, 1194×834): tre kolonner — klokke, dato, vær og
 * lyslinja | «I dag» | Oppgavebunken med kveldens streak-kort og
 * godkjenningskortet under — og «Trykk hvor som helst for å starte» nederst.
 * Under `lg` (telefon i veggmodus) legger kolonnene seg under hverandre. Flaten
 * rulles ikke: første berøring vekker skjermen, så det som ikke får plass
 * kuttes. Veggmodus er laget for nettbrettet.
 *
 * #162 (D6): nederst står «Hvem er du?» (WhoAreYouRow) ved siden av
 * vekkehintet. Et trykk på en person åpner PIN-skjermen; pointerdown der
 * vekker ikke (raden vekker selv i klikket), se wakesOnTap under. Kolonnene krymper (min-h-0) i alle bredder, så det er
 * de som kuttes og ikke raden.
 *
 * Cross-fade: Tailwind `transition-opacity duration-300` for the sleep
 * direction.  Plan 09 motion.css zeros `transition-duration` globally
 * when `prefers-reduced-motion: reduce` matches, so this single
 * Tailwind utility satisfies the reduced-motion contract.
 *
 * Stacking (issue #45) — `z-60`, ABOVE the z-50 modal/sheet tier.  The
 * ambient hub is a device screen state, not a dialog: when the deadman
 * ceiling in useIdleTimer fires with a BottomSheet or a TakeoverScrim open,
 * the hub has to cover them, not paint underneath them.  At z-20 it did the
 * latter — the sheet's own `inset-0` backdrop swallowed every wake tap and
 * the tablet was stuck showing a half-lit sheet nobody could dismiss.  The
 * open sheet stays mounted behind the hub and is revealed again on wake,
 * because an unfinished interaction is exactly what issue #29 gave an idle
 * hold to.  Only SwIdleReload (z-70) sits above this.  See Pattern 7 in
 * docs/ARCHITECTURE.md for the full ladder.
 *
 * Wake propagation (issue #45) — the wake handler deliberately does NOT call
 * `e.stopPropagation()`.  Nothing on this overlay's ancestor path listens for
 * pointerdown (the only other handler in the app is features/shopping/
 * ItemRow.tsx, a sibling under InteractiveMode), so the guard protected
 * nothing.  What it DID stop was the wake tap reaching useIdleTimer's `window`
 * listener, so the tap never moved `lastActivityAt`.  With a sheet's idle hold
 * still live and the deadman baseline left stale, the screen re-idled 60s
 * after every wake, over and over.  On a real touchscreen that is masked,
 * because `touchstart` fires independently and reaches `window` unimpeded; it
 * bites on the mouse/stylus paths, where the rescue depends on a following
 * `click`.
 */
import { useTranslation } from 'react-i18next';
import { ApprovalSlaStrip } from './ApprovalSlaStrip';
import { useDashboard } from './DashboardStore';
import { StreakRiskMount } from './StreakRiskMount';
import { DateTile } from './tiles/DateTile';
import { EventsTile } from './tiles/EventsTile';
import { HomeStatusStrip } from './tiles/HomeStatusStrip';
import { OpenChoresTile } from './tiles/OpenChoresTile';
import { TimeTile } from './tiles/TimeTile';
import { WeatherTile } from './tiles/WeatherTile';
import { useTabletSlaStrip } from './useTabletSlaStrip';
import { WHO_ARE_YOU_MEMBER_ATTR, WhoAreYouRow } from './WhoAreYouRow';

/**
 * Pointerdown på en medlemsknapp i «Hvem er du?» vekker ikke (#162): overlegget
 * ble ellers gjennomsiktig og `pointer-events-none` midt i trykket, og
 * klikket landet i appen under i stedet for på knappen. Hendelsen stoppes
 * fortsatt ikke (#45); vi lar bare være å kalle wake().
 */
function wakesOnTap(target: EventTarget): boolean {
  return !(target instanceof Element && target.closest(`[${WHO_ARE_YOU_MEMBER_ATTR}]`));
}

export function IdleMode() {
  const { t } = useTranslation();
  const isIdle = useDashboard((s) => s.isIdle);
  const wake = useDashboard((s) => s.wake);
  const { count: slaCount } = useTabletSlaStrip();

  return (
    <div
      data-testid="dashboard-idle"
      data-mode="idle"
      role="presentation"
      // No stopPropagation — the tap has to reach useIdleTimer's window
      // listener or the deadman baseline goes stale (issue #45, above).
      onPointerDown={(e) => {
        if (wakesOnTap(e.target)) wake();
      }}
      className={[
        'paper-tex text-ink fixed inset-0 z-60',
        'flex flex-col gap-6 overflow-hidden p-5 lg:p-10',
        'transition-opacity duration-300',
        isIdle
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none',
      ].join(' ')}
    >
      {/* Under lg beholder radene innholdshøyden sin og kuttes nederst
          (auto-rows-max); ellers krympet de inn i hverandre. */}
      <div className="grid min-h-0 flex-1 auto-rows-max gap-6 overflow-hidden lg:auto-rows-auto lg:grid-cols-[380px_minmax(0,1fr)_minmax(0,1fr)]">
        <section aria-label={t('dashboard.idle.clock_aria')} className="flex min-h-0 flex-col gap-4.5">
          <div>
            <TimeTile />
            <DateTile />
          </div>
          {/* Været nederst i kolonnen, som i skissen (margin-top: auto). */}
          <div className="lg:mt-auto">
            <WeatherTile />
          </div>
          {/* Lyslinja: live Hue/Homey-telling, «Hjem: alt rolig» uten leverandør. */}
          <HomeStatusStrip />
        </section>

        <section aria-label={t('dashboard.idle.today')} className="min-h-0">
          <EventsTile />
        </section>

        <section
          aria-label={t('dashboard.chores.title')}
          className="flex min-h-0 flex-col gap-4 overflow-hidden"
        >
          {/* Ukas leder fra topplista; søylene per barn er utelatt (PLAN.md 10a).
              Ingen min-h-0: flisen krymper ikke under innholdet sitt når mange
              streak-kort står under; da kuttes heller bunnen av kolonnen. */}
          <div className="flex-1">
            <OpenChoresTile />
          </div>
          {/* Plan 02-17 StreakRiskFallbackBadge fan-out via StreakRiskMount —
              ett kveldskort per medlem som 20:00-varselet (D-08) flagget. */}
          <StreakRiskMount />
          {/* Plan 02-17 — Tier-2 approval-SLA card. Renders only when count > 0. */}
          <ApprovalSlaStrip count={slaCount} />
        </section>
      </div>

      <div className="flex shrink-0 flex-col gap-4 lg:flex-row lg:items-end lg:gap-6">
        <WhoAreYouRow />
        <p
          data-testid="idle-wake-hint"
          className="text-ink-2 bg-card border-card-line m-0 flex h-14 shrink-0 items-center self-end rounded-pill border px-5.5 text-body font-bold lg:ml-auto"
        >
          {t('dashboard.idle.wake_hint')}
        </p>
      </div>
    </div>
  );
}
